import {action, computed, makeObservable} from 'mobx';

import type {ID, Nullable} from '../../../utils/utility-types';

import {FlagManager} from '../../../utils/store';
import {BaseModel} from '../Base/Base.model';

const ModelName = 'FileModel';

export class FileModel extends BaseModel {
  _fileConfig: Nullable<FileModel.CreateConfig> = null;
  _isUploading = new FlagManager(false);
  _sources: FileModel.Sources;
  description: string;
  fileName: string;
  fileType: FileModel.Type;
  isUploaded: boolean;
  usedByIds: ID[];
  userId: string;
  private EMPTY_SRC: FileModel.Src = {
    mime: '',
    size: 0,
    url: null,
  };

  constructor(props: FileModel.Props) {
    super(props);
    Object.assign(this, props);
    makeObservable(
      this,
      BaseModel.mobxAnnotationFactory<FileModel, FileModel.Props>(props, {
        audioSrc: computed,
        getSrc: action.bound,
        originalSrc: computed,
        playableSrc: computed,
        previewSrc: computed,
      }),
    );
  }

  get audioSrc() {
    if (this.fileType === FileModel.Type.AUDIO) {
      return this.playableSrc ?? this.originalSrc;
    }

    return this.originalSrc;
  }

  static override get modelName(): typeof ModelName {
    return ModelName;
  }

  get modelName(): typeof ModelName {
    return ModelName;
  }

  get originalSrc() {
    return this._sources[FileModel.THUMBS.ORIGINAL];
  }

  get playableSrc() {
    return this._sources[FileModel.THUMBS.PLAYABLE];
  }

  get previewSrc() {
    return this._sources[FileModel.THUMBS.PREVIEW];
  }

  get transcriptSrc() {
    return this._sources[FileModel.THUMBS.TRANSCRIPT];
  }

  getSrc(thumb?: FileModel.THUMBS) {
    const source = thumb ? this._sources[thumb] : null;

    return source ?? this._sources[FileModel.THUMBS.ORIGINAL] ?? this.EMPTY_SRC;
  }

  override update(source: BaseModel.Source<FileModel>) {
    const cachedSource = this.cacheSrc(source);

    return super.update(cachedSource);
  }

  private cacheSrc(source: BaseModel.Source<FileModel>) {
    if (
      source instanceof FileModel &&
      source.fileType === FileModel.Type.IMAGE &&
      source.id === this.id
    ) {
      return source.update({
        _sources: this._sources,
      });
    }

    return source;
  }
}

export namespace FileModel {
  export enum THUMBS {
    IMAGE = 'image',
    ORIGINAL = 'original',
    PLAYABLE = 'playable',
    PREVIEW = 'preview',
    THUMB_250 = 250,
    THUMB_500 = 500,
    THUMB_1000 = 1000,
    TRANSCRIPT = 'transcript',
  }

  export enum Type {
    AUDIO = 'AUDIO',
    DOCUMENT = 'DOCUMENT',
    IMAGE = 'IMAGE',
    LINK = 'LINK',
    VIDEO = 'VIDEO',
  }

  export type CreateConfig = {
    file: File | string;
    meta: {
      mimetype: string;
      name: string;
      size: number;
      uploadingKey?: string;
      url: string;
    };
  };

  export type PreviewSrc = Src & {
    imageSize: {
      height: number;
      width: number;
    };
  };

  export type Props = BaseModel.ModelToProps<
    FileModel,
    | '_fileConfig'
    | '_isUploading'
    | 'audioSrc'
    | 'getSrc'
    | 'originalSrc'
    | 'playableSrc'
    | 'previewSrc'
    | 'transcriptSrc'
  >;

  export type Sources = {
    [FileModel.THUMBS.IMAGE]: Src;
    [FileModel.THUMBS.ORIGINAL]: Src;
    [FileModel.THUMBS.PLAYABLE]: Nullable<Src>;
    [FileModel.THUMBS.PREVIEW]: Nullable<PreviewSrc>;
    [FileModel.THUMBS.THUMB_250]: Src;
    [FileModel.THUMBS.THUMB_500]: Src;
    [FileModel.THUMBS.THUMB_1000]: Src;
    [FileModel.THUMBS.TRANSCRIPT]: Nullable<Src>;
  };

  export type Src = {
    mime: string;
    size: number;
    url: Nullable<string>;
  };
}
